import { useEffect, useMemo, useState } from "react";
import {
  collectFormValues,
  flatFieldList,
  flattenQuestionnaire,
  initialValuesFromSections,
  type QuestionnaireSection,
} from "./questionnaire";
import { saveQuestionnaire, type Questionnaire } from "./api";

interface Props {
  data: Questionnaire | null;
  loading: boolean;
  /** First successful slug — used for save + PDF download. */
  slug: string | null;
  fallbackSlug: string | null;
  onGenerateReport: () => void;
  onPdfDownload: () => void;
  pdfDownloading: boolean;
}

export function Step3Questionnaire({
  data,
  loading,
  slug,
  fallbackSlug,
  onGenerateReport,
  onPdfDownload,
  pdfDownloading,
}: Props) {
  const sections = useMemo<QuestionnaireSection[]>(
    () => (data ? flattenQuestionnaire(data) : []),
    [data],
  );
  const fields = useMemo(() => flatFieldList(sections), [sections]);
  const [values, setValues] = useState<Record<string, string>>(() =>
    initialValuesFromSections(sections),
  );
  const [baseline, setBaseline] = useState(() => JSON.stringify(values));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Re-seed when the underlying questionnaire data is refetched (file_changed
  // event from the server) so the form mirrors disk content.
  useEffect(() => {
    const initial = initialValuesFromSections(sections);
    setValues(initial);
    setBaseline(JSON.stringify(initial));
  }, [sections]);

  const dirty = JSON.stringify(values) !== baseline;

  async function handleSave() {
    setSaveError(null);
    const target = slug ?? fallbackSlug;
    if (!target) {
      setSaveError("Kein Unternehmens-Slug bekannt — Schritt 2 zuerst abschließen.");
      return;
    }
    setSaving(true);
    try {
      const collected = collectFormValues(values, fields);
      const trySlugs = [...new Set([target, fallbackSlug].filter((s): s is string => Boolean(s)))];
      let saved = false;
      let lastError: string | null = null;
      for (const s of trySlugs) {
        try {
          await saveQuestionnaire(s, collected);
          saved = true;
          break;
        } catch (err) {
          lastError = err instanceof Error ? err.message : String(err);
        }
      }
      if (!saved) {
        setSaveError(lastError ?? "Speichern fehlgeschlagen");
        return;
      }
      setBaseline(JSON.stringify(values));
    } finally {
      setSaving(false);
    }
  }

  if (!data || sections.length === 0) {
    return (
      <section className="wizard-section" data-step="3">
        <h2 className="form-title">Schritt 3: Fragebogen prüfen und anpassen</h2>
        <p className="form-description">
          {loading ? "Fragebogen wird geladen…" : "Noch kein Fragebogen vorhanden."}
        </p>
      </section>
    );
  }

  return (
    <section className="wizard-section" data-step="3">
      <h2 className="form-title">Schritt 3: Fragebogen prüfen und anpassen</h2>
      <p className="form-description">
        Passe die Felder bei Bedarf an. Änderungen speichern, dann Bericht generieren.
      </p>

      <div className="form-actions" style={{ marginBottom: "1rem" }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onPdfDownload}
          disabled={pdfDownloading || (!slug && !fallbackSlug)}
        >
          {pdfDownloading ? "Wird erzeugt…" : "Fragebogen als PDF"}
        </button>
      </div>

      <div className="questionnaire-form">
        {sections.map((section) => (
          <div key={section.key} className="questionnaire-section">
            <h3 className="questionnaire-section-title">{section.label}</h3>
            {section.fields.map((field) => (
              <div key={field.path} className="form-row form-row-full">
                <label htmlFor={"q-" + field.path.replace(/\./g, "-")}>{field.label}</label>
                <textarea
                  id={"q-" + field.path.replace(/\./g, "-")}
                  className={
                    "questionnaire-field" + (field.kind === "json" ? " questionnaire-json" : "")
                  }
                  rows={
                    field.kind === "json"
                      ? 4
                      : (values[field.path] ?? "").length > 80
                        ? 3
                        : 1
                  }
                  value={values[field.path] ?? ""}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [field.path]: e.target.value }))
                  }
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      {saveError && <p className="bubble bubble-error">{saveError}</p>}

      <div className="form-actions" style={{ marginTop: "1rem" }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onGenerateReport}
          disabled={loading}
        >
          {loading ? "Wird generiert…" : "Bericht generieren"}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void handleSave()}
          disabled={!dirty || saving}
        >
          {saving ? "Speichern…" : "Änderungen speichern"}
        </button>
      </div>
    </section>
  );
}
